import { normalizeReadingProgress } from '@/utils/readingProgress'

export interface ReaderNavigationState {
  initialProgress?: number
  progressSource?: string
}

type ReaderNavigationStateOptions = {
  bookId: number | string
  explicitProgress?: unknown
  readingProgress?: Record<string, unknown> | null | undefined
}

/**
 * 统一构建阅读页入口进度参数，优先使用显式进度，其次回退到本地已知进度。
 */
export const buildReaderNavigationState = ({
  bookId,
  explicitProgress,
  readingProgress
}: ReaderNavigationStateOptions): ReaderNavigationState | undefined => {
  const normalizeBookId = String(bookId || '').trim()
  if (!normalizeBookId) return undefined

  const clampProgress = (value: unknown): number => normalizeReadingProgress(value)

  const readProgressObject = (key: string): number => {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return 0
      const parsed = JSON.parse(raw) as { percentage?: unknown; progress?: unknown }
      return clampProgress(parsed?.percentage ?? parsed?.progress)
    } catch {
      return 0
    }
  }

  const readStringProgress = (key: string): number => {
    try {
      return clampProgress(localStorage.getItem(key))
    } catch {
      return 0
    }
  }

  const explicitPct = clampProgress(explicitProgress)
  if (explicitPct > 0.001) {
    return {
      initialProgress: explicitPct,
      progressSource: 'navigation'
    }
  }

  const storePct = clampProgress(readingProgress?.[normalizeBookId])

  let namespacedPct = 0
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = String(localStorage.key(index) || '')
      if (!key.endsWith(`:${normalizeBookId}`)) continue
      if (!key.startsWith('reader_progress:') && !key.startsWith('reader_progress_backup:')) continue
      namespacedPct = Math.max(namespacedPct, readProgressObject(key))
    }
  } catch {
    namespacedPct = 0
  }

  const progress = Math.max(
    storePct,
    namespacedPct,
    readProgressObject(`reader_progress:${normalizeBookId}`),
    readProgressObject(`reader_progress_backup:${normalizeBookId}`),
    readStringProgress(`pdf-${normalizeBookId}-scrollProgress`),
    readStringProgress(`azw3-${normalizeBookId}-scrollProgress`),
    readStringProgress(`mobi-${normalizeBookId}-scrollProgress`)
  )

  if (progress <= 0.001) return undefined

  return {
    initialProgress: progress,
    progressSource: 'stored'
  }
}
